import { Invoice, InvoiceSource } from "@common/types/invoice";
import { InvoiceView } from "@common/types/views/invoiceViews";
import { View } from "@common/types/views/views";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionBar } from "@components/views/BulkSelectionBar";
import { Button } from "@design-system/Button/Button";
import { StandardModal } from "@design-system/Modal/Modal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { CreateCounterpartyModal } from "@components/counterparty/CreateCounterpartyModal";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import React from "react";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { useViewTableLoadMore } from "@hooks/useLoadMoreTableState";
import { PaginatedInvoicesTableContext } from "../../PaginatedInvoicesTable.context";
import { useInvoiceTableEmptyView } from "../../useInvoiceTableEmptyView";
import { BulkVoidInvoicesModal } from "../../InvoicesBulkActions/BulkVoidInvoicesModal";
import { BulkDeleteInvoicesModal } from "../../InvoicesBulkActions/BulkDeleteInvoicesModal";
import { BulkUncollectibleInvoicesModal } from "../../InvoicesBulkActions/BulkUncollectibleInvoicesModal";
import { invoiceBooksToBadDebt } from "../../invoiceBooksToBadDebt";
import { useInvoiceBulkActions } from "../../InvoicesBulkActions/useInvoiceBulkActions";
import { useInvoiceBulkDelete } from "../../InvoicesBulkActions/useInvoiceBulkDelete";
import { useInvoiceBulkCommandActions } from "../../InvoicesBulkActions/useInvoiceBulkCommandActions";
import { useInvoiceBulkCounterpartyOptions } from "../../InvoicesBulkActions/useInvoiceBulkCounterpartyOptions";
import { useInvoiceBulkCreateTask } from "../../InvoicesBulkActions/useInvoiceBulkCreateTask";
import { useInvoiceBulkScheduleOptions } from "../../InvoicesBulkActions/useInvoiceBulkScheduleOptions";
import { useInvoicesBulkDownloadPdf } from "../../InvoicesBulkActions/useInvoicesBulkDownloadPdf";
import { useInvoicesFooterPassingStatistics } from "../../useInvoicesFooterPassingStatistics";
import { InvoicesTotals } from "../../InvoicesTotals";
import type { ViewTableRowDragOut } from "@design-system/ViewTable/ViewTable";
import { getSelectedInvoices } from "./getSelectedInvoices";
import { InvoicesView } from "./InvoicesView";
import { InvoicesViewCallbacks } from "./types";

interface InvoicesViewContainerProps {
    view: InvoiceView;
    onInvoiceClick: (invoice: Invoice) => void;
    selectedInvoiceId?: string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    onClearSelection?: () => void;
    onEditInvoices?: (invoices: Invoice[]) => void;
    isSchedulesEnabled?: boolean;
    prioritizedScheduleTemplateId?: string;
    onCreateSchedule?: InvoicesViewCallbacks["onCreateSchedule"];
    onScheduleApplied?: InvoicesViewCallbacks["onScheduleApplied"];
    onRequestScheduleConfirm?: InvoicesViewCallbacks["onRequestScheduleConfirm"];
    onRequestLifecycleAction?: InvoicesViewCallbacks["onRequestLifecycleAction"];
    hasConnectedInvoiceAccounts: boolean;
    onClearFilters: () => void;
    /** Replaces the default empty state (task tables have their own). */
    emptyView?: () => React.ReactNode;
    onViewChange?: (view: View) => void;
    /** Extra controls rendered next to the bulk actions button. */
    renderSelectionActions?: (selectedInvoices: Invoice[]) => React.ReactNode;
    rowDragOut?: ViewTableRowDragOut<Invoice>;
}

export const InvoicesViewContainer: React.FC<InvoicesViewContainerProps> = ({
    view,
    onInvoiceClick,
    selectedInvoiceId,
    rowSelection,
    onRowSelectionChange,
    onClearSelection,
    onEditInvoices,
    isSchedulesEnabled = false,
    prioritizedScheduleTemplateId,
    onCreateSchedule,
    onScheduleApplied,
    onRequestScheduleConfirm,
    onRequestLifecycleAction,
    hasConnectedInvoiceAccounts,
    onClearFilters,
    emptyView: emptyViewOverride,
    onViewChange,
    renderSelectionActions,
    rowDragOut,
}) => {
    const {
        invoices,
        invoiceCount,
        currentSort,
        handleSortChange,
        currentPage,
        totalPages,
        setPage,
        isLoading,
        loadMore,
    } = React.useContext(PaginatedInvoicesTableContext);
    const tableLoadMore = useViewTableLoadMore(
        loadMore,
        "Failed to load more invoices",
    );
    const hasPagination = !loadMore && totalPages > 1;

    const { emptyView, shouldHideFooter } = useInvoiceTableEmptyView({
        isLoading,
        filteredCount: invoiceCount,
        hasConnectedInvoiceAccounts,
        onClearFilters,
        viewKind: "invoices",
    });

    const selectedInvoices = React.useMemo(
        () => getSelectedInvoices(invoices, rowSelection),
        [rowSelection, invoices],
    );

    const doAllSelectedBookToBadDebt = React.useMemo(
        () =>
            selectedInvoices.length > 0 &&
            selectedInvoices.every((invoice) =>
                invoiceBooksToBadDebt(invoice.currency),
            ),
        [selectedInvoices],
    );

    // The bulk Edit action routes selections through the importer-only patch
    // flow (ManualInvoiceEditDrawer), so it is only offered when every selected
    // invoice is importer-sourced. This mirrors the line-items edit gate
    // (isLineItemsEditAvailable) and keeps non-importer invoices — now
    // selectable for Mark Void and other bulk status actions — out of it.
    const isBulkEditAvailable = React.useMemo(
        () =>
            selectedInvoices.length > 0 &&
            selectedInvoices.every(
                (invoice) => invoice.source === InvoiceSource.IMPORTER,
            ),
        [selectedInvoices],
    );

    const {
        openBulkVoidModal,
        openBulkUncollectibleModal,
        closeBulkStatusModal,
        confirmBulkStatusDate,
        pendingStatusAction,
        bulkStatusLockDate,
        bulkStatusMinInvoiceDate,
        isBulkStatusLockResolved,
        isProcessingBulkStatus,
        canBulkChangeCounterparty,
        confirmBulkChangeCounterparty,
        isProcessingBulkChangeCounterparty,
    } = useInvoiceBulkActions({
        selectedInvoices,
        onActionComplete: onClearSelection,
    });

    const {
        isBulkDeleteConfirmOpen,
        deletableCount,
        skippedCount,
        canBulkDelete,
        openBulkDeleteModal,
        closeBulkDeleteModal,
        confirmBulkDelete,
        isProcessingBulkDelete,
    } = useInvoiceBulkDelete({
        selectedInvoices,
        onActionComplete: onClearSelection,
    });

    const {
        counterpartyOptions,
        isCreateCounterpartyOpen,
        closeCreateCounterparty,
        handleCreateCounterpartyClosed,
    } = useInvoiceBulkCounterpartyOptions({
        onSelectCounterparty: confirmBulkChangeCounterparty,
    });

    const {
        handleDownload: handleBulkDownloadPdf,
        isDownloading: isDownloadingPdf,
        allowDownload: canDownloadPdf,
        downloadDisabledTooltip,
    } = useInvoicesBulkDownloadPdf({ selectedInvoices });

    const isBulkActionPending =
        isProcessingBulkStatus ||
        isProcessingBulkChangeCounterparty ||
        isProcessingBulkDelete ||
        isDownloadingPdf;

    const {
        options: bulkScheduleOptions,
        confirmModal: bulkScheduleConfirmModal,
    } = useInvoiceBulkScheduleOptions({
        selectedInvoices,
        isEnabled: isSchedulesEnabled,
        isBusy: isBulkActionPending,
        onClearSelection,
    });

    // Invoices are paginated server-side, so only the current page is loaded.
    // Selection state persists across pages, but bulk actions can only act on
    // the loaded rows — so clear the selection when the page changes to keep
    // the footer count and the actionable set in sync.
    const handlePageChange = React.useCallback(
        (nextPage: number) => {
            onClearSelection?.();
            setPage(nextPage);
        },
        [onClearSelection, setPage],
    );

    const handleCreateTask = useInvoiceBulkCreateTask({
        view,
        selectedInvoices,
    });

    const { handleOpenBulkActions } = useInvoiceBulkCommandActions({
        selectedCount: selectedInvoices.length,
        isBusy: isBulkActionPending,
        onMarkVoid: openBulkVoidModal,
        onMarkUncollectible: openBulkUncollectibleModal,
        invoiceBulkCounterpartyOptions: counterpartyOptions,
        invoiceBulkScheduleOptions: isSchedulesEnabled
            ? bulkScheduleOptions
            : undefined,
        canChangeCounterparty: canBulkChangeCounterparty,
        onDownloadPdf: handleBulkDownloadPdf,
        canDownloadPdf,
        downloadPdfDisabledTooltip: downloadDisabledTooltip,
        onDelete: openBulkDeleteModal,
        canDelete: canBulkDelete,
        onCreateTask: handleCreateTask,
    });

    // Same scoping as the Transactions command bar total (PRO-11992): shows
    // the selected invoices' total when any are selected, otherwise every
    // invoice matching the current filters (not just the current page).
    const passingStatistics = useInvoicesFooterPassingStatistics({
        rowSelection,
    });

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {hasPagination && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
                {passingStatistics && <InvoicesTotals {...passingStatistics} />}
                <BulkSelectionBar
                    items={invoices}
                    rowSelection={rowSelection}
                    onClearSelection={onClearSelection}
                    onEditItems={
                        isBulkEditAvailable ? onEditInvoices : undefined
                    }
                />
            </HStack>
            {selectedInvoices.length > 0 && (
                <HStack gap={8}>
                    <Tooltip content="Open bulk actions">
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleOpenBulkActions}
                            disabled={isBulkActionPending}
                            rightIcon={
                                <span data-slot-box="">
                                    <Shortcut keys="mod+k" />
                                </span>
                            }
                        >
                            Actions
                        </Button>
                    </Tooltip>
                    {renderSelectionActions?.(selectedInvoices)}
                </HStack>
            )}
        </CommandIsland>
    );

    return (
        <>
            <InvoicesView
                view={view}
                invoices={invoices ?? null}
                selectedInvoiceId={selectedInvoiceId}
                onInvoiceClick={onInvoiceClick}
                sort={currentSort}
                onSortChange={handleSortChange}
                footer={shouldHideFooter ? undefined : footer}
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                isSchedulesEnabled={isSchedulesEnabled}
                prioritizedScheduleTemplateId={prioritizedScheduleTemplateId}
                onCreateSchedule={onCreateSchedule}
                onScheduleApplied={onScheduleApplied}
                onRequestScheduleConfirm={onRequestScheduleConfirm}
                onRequestLifecycleAction={onRequestLifecycleAction}
                emptyView={emptyViewOverride ?? emptyView}
                onViewChange={onViewChange}
                rowDragOut={rowDragOut}
                loadMore={tableLoadMore}
            />
            <BulkVoidInvoicesModal
                show={pendingStatusAction?.kind === "void"}
                selectedCount={selectedInvoices.length}
                isProcessing={isProcessingBulkStatus}
                minDate={bulkStatusMinInvoiceDate}
                lockDate={bulkStatusLockDate}
                isLockResolved={isBulkStatusLockResolved}
                onCancel={closeBulkStatusModal}
                onConfirm={confirmBulkStatusDate}
            />
            <BulkUncollectibleInvoicesModal
                show={pendingStatusAction?.kind === "uncollectible"}
                selectedCount={selectedInvoices.length}
                allBookToBadDebt={doAllSelectedBookToBadDebt}
                isProcessing={isProcessingBulkStatus}
                minDate={bulkStatusMinInvoiceDate}
                lockDate={bulkStatusLockDate}
                isLockResolved={isBulkStatusLockResolved}
                onCancel={closeBulkStatusModal}
                onConfirm={confirmBulkStatusDate}
            />
            <BulkDeleteInvoicesModal
                show={isBulkDeleteConfirmOpen}
                deletableCount={deletableCount}
                skippedCount={skippedCount}
                onCancel={closeBulkDeleteModal}
                onConfirm={confirmBulkDelete}
            />
            <StandardModal
                show={isCreateCounterpartyOpen}
                onHide={closeCreateCounterparty}
                size="lg"
            >
                <CreateCounterpartyModal
                    close={handleCreateCounterpartyClosed}
                    forTransactionEditing
                />
            </StandardModal>
            {bulkScheduleConfirmModal}
        </>
    );
};
